import type React from 'react';

import { Button, Spinner, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import type { MediaHitEmbed } from '../../../../shared/context';
import { useMediaHitsPermissions } from '../../../../shared/hooks/useMediaHitsPermissions';
import { MediaHitContent } from '../../../MediaHitList/components/MediaHit';

export const MediaHitsModalList: React.FC<MediaHitsModalListProps> = ({
	embeds,
	status,
	hasNextPage,
	hasLoadedMore,
	onLoadMore,
	removeMedia,
}) => {
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	if (status === 'loading') {
		return (
			<div className="flex w-full justify-center py-8" data-testid="media-hits-modal-loading">
				<Spinner />
			</div>
		);
	}

	// same collapse rule as the preview carousel (MediaHit.tsx): invalid embeds only show for staff,
	// so a list where every hit failed reads as empty rather than a wall of warnings
	const visibleEmbeds = embeds.filter((embed) => embed.status !== 'invalid' || canDelete);

	return (
		<div className="flex w-full flex-col gap-4">
			{visibleEmbeds.map((embed) => (
				<div key={embed.id} data-testid={`media-hits-modal-row-${embed.id}`} className="group relative border-none">
					<MediaHitContent media={embed} removeMedia={removeMedia} deleteButtonPosition="overlay" />
				</div>
			))}
			{status === 'loaded' && visibleEmbeds.length === 0 && !hasNextPage && (
				<Text as="p" className="py-4">
					<Translate value="corgi.petition_gamma.media_mentions_modal.empty_state" />
				</Text>
			)}
			{status === 'error' && (
				<div className="flex w-full justify-center py-8" role="alert">
					<Text as="p" className="py-4">
						<Translate value="corgi.petition_gamma.media_mentions_modal.error" />
					</Text>
				</div>
			)}
			{status === 'loadingMore' && (
				<>
					<div className="sr-only" role="alert">
						<Translate value="corgi.petition_gamma.media_mentions_modal.fetching_more" />
					</div>
					<Spinner className="self-center" />
				</>
			)}
			{status === 'loaded' && hasLoadedMore && (
				<div className="sr-only" role="alert">
					<Translate value="corgi.petition_gamma.media_mentions_modal.fetched_more" />
				</div>
			)}
			{status === 'loaded' && hasNextPage && (
				<Button variant="secondary" onClick={onLoadMore} data-qa="media-hits-modal-view-more">
					{/* Borrowing the signed-petition-home "view more" copy until this button gets its own approved translations. */}
					<Translate value="corgi.signed_petition_home.media_mentions_modal.view_more_button" />
				</Button>
			)}
		</div>
	);
};

type MediaHitsModalListProps = {
	embeds: readonly MediaHitEmbed[];
	status: 'error' | 'loaded' | 'loading' | 'loadingMore';
	hasNextPage: boolean;
	hasLoadedMore: boolean;
	onLoadMore: () => void;
	removeMedia: (mediaId: string) => void;
};
